import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../../services/adminApiService';
import { Clock, Search, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';

export const AdminTrialsView: React.FC = () => {
  const [trials, setTrials] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadTrials = async () => {
    setLoading(true);
    try {
      const data = await adminApiService.getTrials();
      if (data.success) {
        setTrials(data.trials || []);
      }
    } catch (err) {
      console.error('Failed to load trials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrials();
  }, []);

  const filtered = trials.filter(
    (t) =>
      t.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      t.customerEmail?.toLowerCase().includes(search.toLowerCase()) ||
      t.tenant?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            <span>Deneme Sürümü (Trial) Hesapları</span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            14 günlük ücretsiz deneme sürecindeki müşterileri, kalan sürelerini ve dönüşüm potansiyellerini takip edin.
          </p>
        </div>
        <button
          onClick={loadTrials}
          disabled={loading}
          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center gap-2 self-start"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          <span>Yenile</span>
        </button>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Müşteri adı, e-posta veya şirket adı ile ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500/20 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-3 px-4">Müşteri</th>
                <th className="py-3 px-4">Şirket / Tenant</th>
                <th className="py-3 px-4 text-center">Site Sayısı</th>
                <th className="py-3 px-4">Başlangıç</th>
                <th className="py-3 px-4">Bitiş Tarihi</th>
                <th className="py-3 px-4">Kalan Süre</th>
                <th className="py-3 px-4">Durum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-amber-500" />
                    Trial hesapları yükleniyor...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400">
                    Aktif deneme sürümü hesabı bulunmuyor.
                  </td>
                </tr>
              ) : (
                filtered.map((t) => (
                  <tr key={t.userId} className="hover:bg-slate-50/70 transition">
                    <td className="py-3.5 px-4 font-semibold text-slate-900">
                      <div>{t.customerName}</div>
                      <div className="text-[11px] text-slate-400">{t.customerEmail}</div>
                    </td>
                    <td className="py-3.5 px-4 text-slate-700">
                      {t.tenant}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-50 text-amber-700 font-bold text-xs">
                        {t.websiteCount}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-slate-500 font-mono text-[11px]">
                      {t.startDateFormatted}
                    </td>
                    <td className="py-3.5 px-4 text-slate-700 font-mono text-[11px]">
                      {t.endDateFormatted}
                    </td>
                    <td className="py-3.5 px-4 font-bold">
                      <span className={`px-2 py-0.5 rounded text-[11px] ${
                        t.remainingDays <= 2 ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'
                      }`}>
                        {t.remainingDays} gün kaldı
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      {t.status === 'Aktif Trial' ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                          <CheckCircle2 className="w-3 h-3" />
                          Aktif
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded-md">
                          <AlertTriangle className="w-3 h-3" />
                          Süresi Doldu
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
